import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Button, Typography, TextField, MenuItem, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import GoogleMapsAutoComplete from './GoogleMapsAutoComplete';
// place/flag icons replaced by inline gold pin SVG for consistent styling
import DriveEtaIcon from '@mui/icons-material/DriveEta';
import InputAdornment from '@mui/material/InputAdornment';
import AppStoreBadge from '../assets/img/appstore.png';
import PlayStoreBadge from '../assets/img/playstore.png';
import { useNavigate } from 'react-router-dom';
import { colors } from './Theme/WebTheme';

export default function QuickBooking(props) {
  const { localT, settings, classes, loggedIn, isMobile, hideCta } = props;
  const accentColor = props.accentColor || colors.Button_Primary;
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const storeSettings = useSelector(state => state.settingsdata && state.settingsdata.settings);
  const cartypes = useSelector(state => state.cartypes);
  const [pickupText, setPickupText] = useState('');
  const [dropText, setDropText] = useState('');
  const [pickupSel, setPickupSel] = useState(null);
  const [dropSel, setDropSel] = useState(null);
  const [vehicleType, setVehicleType] = useState(props.initialVehicle || '');
  const [fieldErrors, setFieldErrors] = useState({ pickup: '', drop: '' });
  const { i18n } = useTranslation();

  // Memoïser langCode et vehiclePlaceholder
  const langCode = useMemo(() =>
    (i18n?.language ? i18n.language.slice(0, 2).toLowerCase() : 'en'),
    [i18n?.language]
  );

  const vehiclePlaceholder = useMemo(() => {
    const map = { en: 'Choose vehicle', fr: 'Choisir un véhicule' };
    return map[langCode] || map.en;
  }, [langCode]);

  const quickBookingLabels = useMemo(() => ({
    pickupRequired: langCode === 'fr' ? 'Indiquez une adresse de départ.' : 'Enter a pickup address.',
    dropRequired: langCode === 'fr' ? 'Indiquez une destination.' : 'Enter a destination.',
    loginHint: langCode === 'fr' ? 'Connectez-vous pour finaliser votre réservation.' : 'Sign in to finish your booking.'
  }), [langCode]);

  const settingsFinal = settings || storeSettings || {};

  // Fonction formatAmount optimisée avec useCallback
  const formatAmount = useCallback((value, decimal, country) => {
    const number = parseFloat(value || 0);
    const locale = country === "Vietnam" ? "vi-VN" : "en-US";
    return number.toLocaleString(locale, {
      minimumFractionDigits: decimal,
      maximumFractionDigits: decimal
    });
  }, []);
  const navigate = useNavigate();

  useEffect(() => {
    // if parent pre-filled texts provided, use them
    if (props.initialPickup) setPickupText(props.initialPickup);
    if (props.initialDrop) setDropText(props.initialDrop);
  }, [props.initialPickup, props.initialDrop]);

  // Ajouter des styles globaux pour l'autocomplétion dans QuickBooking
  useEffect(() => {
    const styleId = 'quickBooking-global-styles';
    if (!document.getElementById(styleId)) {
      const globalStyles = document.createElement('style');
      globalStyles.id = styleId;
      globalStyles.textContent = `
        /* Z-index élevé pour les dropdowns d'autocomplétion */
        .MuiAutocomplete-popper {
          z-index: 10000 !important;
        }
        .MuiAutocomplete-listbox {
          z-index: 10000 !important;
          background-color: #2d2d2d !important;
          border: 1px solid #444 !important;
          border-radius: 8px !important;
          box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
        }
        /* Éviter les divs vides dans l'autocomplétion */
        .MuiAutocomplete-noOptions {
          display: none !important;
        }
        /* Style pour les options d'autocomplétion - texte lisible */
        .MuiAutocomplete-option {
          background-color: #2d2d2d !important;
          color: #ffffff !important;
          border-bottom: 1px solid rgba(255,255,255,0.1) !important;
          padding: 12px 16px !important;
        }
        .MuiAutocomplete-option:hover {
          background-color: #404040 !important;
          color: #ffffff !important;
        }
        .MuiAutocomplete-option[aria-selected="true"] {
          background-color: #1976d2 !important;
          color: #ffffff !important;
        }
        /* Paper du dropdown */
        .MuiAutocomplete-paper {
          background-color: #2d2d2d !important;
          border: 1px solid #444 !important;
          border-radius: 8px !important;
        }
      `;
      document.head.appendChild(globalStyles);
    }

    return () => {
      const existingStyles = document.getElementById(styleId);
      if (existingStyles) {
        existingStyles.remove();
      }
    };
  }, []);

  const cleanLocationObject = useCallback((obj) => {
    if (!obj || typeof obj !== 'object') return obj;

    const clean = {};
    const allowedProps = ['description', 'place_id', 'id', 'lat', 'lon', 'coords', 'source'];

    allowedProps.forEach(prop => {
      if (Object.prototype.hasOwnProperty.call(obj, prop) && obj[prop] !== undefined) {
        clean[prop] = obj[prop];
      }
    });

    return clean;
  }, []);

  const buildBookingState = useCallback(() => {
    let carData = null;
    let vehicleTypeToSend = vehicleType;
    if (cartypes?.cars?.length > 0) {
      carData = cartypes.cars.find(c => (c.name === vehicleType || c.id === vehicleType)) || null;
      if (!carData) {
        carData = cartypes.cars[0];
      }
      if (!vehicleTypeToSend && carData?.name) vehicleTypeToSend = carData.name;
    }

    const cleanCarData = carData ? {
      id: carData.id,
      name: carData.name,
      rate_per_unit_distance: carData.rate_per_unit_distance,
      min_fare: carData.min_fare,
      extra_info: carData.extra_info,
      image: carData.image,
      parcelTypes: carData.parcelTypes,
      options: carData.options
    } : null;

    return {
      pickupText: pickupText.trim(),
      dropText: dropText.trim(),
      pickupSel: cleanLocationObject(pickupSel),
      dropSel: cleanLocationObject(dropSel),
      quickBook: true,
      vehicleType: vehicleTypeToSend,
      carData: cleanCarData
    };
  }, [cartypes, cleanLocationObject, dropSel, dropText, pickupSel, pickupText, vehicleType]);

  const handleBook = useCallback(() => {
    const nextErrors = {
      pickup: (pickupText || pickupSel?.description || '').trim() ? '' : quickBookingLabels.pickupRequired,
      drop: (dropText || dropSel?.description || '').trim() ? '' : quickBookingLabels.dropRequired
    };

    setFieldErrors(nextErrors);
    if (nextErrors.pickup || nextErrors.drop) return;

    const bookingState = buildBookingState();

    if (!loggedIn) {
      try {
        window.sessionStorage.setItem('pendingQuickBooking', JSON.stringify(bookingState));
      } catch (e) {}
      navigate('/login', {
        state: {
          redirectTo: '/addbookings',
          bookingState,
          message: quickBookingLabels.loginHint
        }
      });
      return;
    }

    navigate('/addbookings', { state: bookingState });
  }, [buildBookingState, dropSel?.description, dropText, loggedIn, navigate, pickupSel?.description, pickupText, quickBookingLabels]);

  const compactMobile = isMobile || isSmallScreen;

  return (
    <Box sx={{ position: 'relative', width: '100%', maxWidth: compactMobile ? 430 : '100%', mx: 'auto', padding: 0 }}>
  <div className={classes && classes.glassCard ? classes.glassCard : ''} style={{
    position: 'relative',
    background: compactMobile ? 'rgba(0,0,0,0.78)' : 'rgba(0,0,0,0.72)',
    border: compactMobile ? '1px solid rgba(255,255,255,0.08)' : 'none',
    padding: compactMobile ? 18 : 12,
    maxWidth: '100%',
    width: '100%',
    boxSizing: 'border-box',
    textAlign: 'center',
    overflow: 'hidden',
    borderRadius: compactMobile ? 20 : 12,
    backdropFilter: 'saturate(120%) blur(8px)',
    boxShadow: compactMobile ? '0 20px 48px rgba(0,0,0,0.38)' : undefined,
    marginLeft: 0
  }}>
        <Typography variant="h6" style={{
          fontWeight: 900,
          marginBottom: compactMobile ? 14 : 8,
          color: '#fff',
          textAlign: 'center',
          fontSize: compactMobile ? '20px' : '20px',
          lineHeight: 1.2
        }}>{(localT && localT('quick_book')) || 'Quick Book'}</Typography>
  <div style={{ color: colors.WHITE }}>
          <GoogleMapsAutoComplete
            className={classes && classes.formInput}
            placeholder={(localT && localT('pickup_place')) || 'Pickup'}
            variant="outlined"
            freeSolo
            value={pickupSel}
            onChange={(val) => {
              setPickupSel(val);
              setFieldErrors(prev => ({ ...prev, pickup: '' }));
              if (val && val.description) setPickupText(val.description);
              else if (!val) setPickupText('');
            }}
            onInputChange={(raw) => {
              setPickupText(raw);
              if (raw && raw.trim()) setFieldErrors(prev => ({ ...prev, pickup: '' }));
            }}
            startAdornment={<span style={{display:'inline-flex', alignItems:'center', marginRight:8}}><svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill={accentColor}/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></span>}
            inputTextColor={colors.WHITE}
          />
          {fieldErrors.pickup ? (
            <Typography variant="caption" sx={{ color: '#ffb4a8', display: 'block', textAlign: 'left', mt: 0.5, mb: 1 }}>
              {fieldErrors.pickup}
            </Typography>
          ) : null}
          <GoogleMapsAutoComplete
            className={classes && classes.formInput}
            placeholder={(localT && localT('drop_place')) || 'Drop'}
            variant="outlined"
            freeSolo
            value={dropSel}
            onChange={(val) => {
              setDropSel(val);
              setFieldErrors(prev => ({ ...prev, drop: '' }));
              if (val && val.description) setDropText(val.description);
              else if (!val) setDropText('');
            }}
            onInputChange={(raw) => {
              setDropText(raw);
              if (raw && raw.trim()) setFieldErrors(prev => ({ ...prev, drop: '' }));
            }}
            startAdornment={<span style={{display:'inline-flex', alignItems:'center', marginRight:8}}><svg width="16" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill={accentColor}/><circle cx="12" cy="9" r="2.5" fill="#fff"/></svg></span>}
            inputTextColor={colors.WHITE}
          />
          {fieldErrors.drop ? (
            <Typography variant="caption" sx={{ color: '#ffb4a8', display: 'block', textAlign: 'left', mt: 0.5, mb: 1 }}>
              {fieldErrors.drop}
            </Typography>
          ) : null}

          {/* Vehicle selector for quick-book */}
          <TextField
            select
            fullWidth
            value={vehicleType}
            onChange={(e) => setVehicleType(e.target.value)}
            variant="outlined"
            className={classes && classes.formInput}
            sx={{ mt: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <DriveEtaIcon style={{ color: accentColor }} />
                </InputAdornment>
              ),
              style: { color: colors.WHITE }
            }}
            SelectProps={{
              displayEmpty: true,
              renderValue: (selected) => {
                if (!selected) return <span style={{ color: 'rgba(255,255,255,0.75)' }}>{vehiclePlaceholder}</span>;
                return <span style={{ color: colors.WHITE }}>{selected}</span>;
              },
              MenuProps: {
                PaperProps: {
                  sx: { backgroundColor: colors.BLACK, color: colors.WHITE }
                }
              }
            }}
          >
            {/* Prefer cartypes.cars from Redux for full vehicle info, otherwise fallback to simple list */}
            {(cartypes && cartypes.cars && cartypes.cars.length > 0 ? cartypes.cars : (props.vehicleOptions || (settingsFinal && settingsFinal.vehicleTypes) || ['Standard', 'Premium', 'Van'])).map((v, idx) => {
              // If cartype object, render detailed item
              if (v && typeof v === 'object' && v.name) {
                const rate = v.rate_per_unit_distance;
                const minFare = v.min_fare;
                return (
                  <MenuItem key={v.id || idx} value={v.name} sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                    {v.image ? <img src={v.image} alt={v.name} style={{ width: 72, height: 50, objectFit: 'cover', borderRadius: 8, marginRight: 12 }} /> : <div style={{ width: 72, height: 50, background: '#222', borderRadius: 8, marginRight: 12 }} />}
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <Typography style={{ color: colors.WHITE, fontWeight: 700 }}>{(localT && localT(v.name)) || v.name}</Typography>
                      <Typography style={{ color: accentColor, fontSize: 13 }}>{settingsFinal && settingsFinal.swipe_symbol === false ? `${settingsFinal.symbol}${formatAmount(rate, settingsFinal.decimal || 2, settingsFinal.country)}` : `${formatAmount(rate, settingsFinal.decimal || 2, settingsFinal.country)}${settingsFinal && settingsFinal.symbol ? settingsFinal.symbol : '€'}`}/{settingsFinal && settingsFinal.convert_to_mile ? (localT && localT('mile')) || 'mile' : (localT && localT('km')) || 'km'}</Typography>
                      <Typography style={{ color: colors.WHITE, fontSize: 12 }}>{(localT && localT('min_fare')) || 'Tarif minimum'}: <span style={{ color: accentColor }}>{settingsFinal && settingsFinal.swipe_symbol === false ? `${settingsFinal.symbol}${formatAmount(minFare, settingsFinal.decimal || 2, settingsFinal.country)}` : `${formatAmount(minFare, settingsFinal.decimal || 2, settingsFinal.country)}${settingsFinal && settingsFinal.symbol ? settingsFinal.symbol : '€'}`}</span></Typography>
                      {v.extra_info ? <Typography style={{ color: colors.WHITE, fontSize: 12 }}>{(localT && localT('extra_info')) || 'Info'}: <span style={{ color: colors.WHITE }}>{v.extra_info}</span></Typography> : null}
                    </div>
                  </MenuItem>
                );
              }
              // fallback simple string item
              return (
                <MenuItem key={v || idx} value={v}>
                  {v}
                </MenuItem>
              );
            })}
          </TextField>

          {!hideCta && <div style={{
            display: 'flex',
            gap: compactMobile ? 10 : 10,
            marginTop: compactMobile ? 14 : 8,
            justifyContent: 'center',
            flexDirection: compactMobile ? 'column' : 'row'
          }}>
            <Button
              variant="contained"
              className={classes && classes.ctaButton ? classes.ctaButton : ''}
              sx={{
                flex: 1,
                minHeight: compactMobile ? 48 : undefined,
                fontSize: compactMobile ? '13px' : '16px',
                padding: compactMobile ? '12px 14px' : '12px 22px',
                background: accentColor,
                borderRadius: compactMobile ? 2.5 : undefined,
                letterSpacing: 0,
                whiteSpace: 'normal',
                '&:hover': { background: accentColor }
              }}
              onClick={handleBook}
            >
              {(localT && localT('book_now')) || 'BOOK NOW'}
            </Button>
            <Button
              variant="outlined"
              className={classes && classes.secondaryButton ? classes.secondaryButton : ''}
              sx={{
                flex: 1,
                minHeight: compactMobile ? 46 : undefined,
                fontSize: compactMobile ? '13px' : '16px',
                padding: compactMobile ? '11px 14px' : '8px 18px',
                borderRadius: compactMobile ? 2.5 : undefined,
                letterSpacing: 0
              }}
              onClick={() => navigate('/contact-us')}
            >
              {(localT && localT('contact_us')) || 'Contact'}
            </Button>
          </div>}

          {hideCta && (
            <div style={{ marginTop: compactMobile ? 14 : 8 }}>
              <Button
                variant="contained"
                fullWidth
                className={classes && classes.ctaButton ? classes.ctaButton : ''}
                sx={{
                  minHeight: 52,
                  fontSize: '15px',
                  padding: '13px 22px',
                  background: accentColor,
                  color: '#000',
                  borderRadius: 2.5,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: 'none',
                  boxShadow: '0 8px 20px rgba(234,179,8,0.3)',
                  '&:hover': { background: accentColor, transform: 'scale(1.01)' }
                }}
                onClick={handleBook}
              >
                {(localT && localT('book_now')) || 'Voir les prix & Réserver'}
              </Button>
            </div>
          )}

          <Typography variant="caption" style={{
            display: 'block',
            marginTop: compactMobile ? 14 : 10,
            opacity: 0.8,
            textAlign: 'center',
            fontSize: compactMobile ? '12px' : '14px',
            lineHeight: 1.4
          }}>{(localT && localT('quick_book_note')) || ''}</Typography>

          <div style={{
            marginTop: compactMobile ? 18 : 20,
            textAlign: 'center',
            display: 'flex',
            gap: compactMobile ? 12 : 8,
            justifyContent: 'center'
          }}>
            <a href={settings && settings.AppleStoreLink ? settings.AppleStoreLink : '#'} style={{ display: 'inline-block' }} aria-label="Apple Store">
              <img src={AppStoreBadge} alt="Apple Store Link" style={{
                height: compactMobile ? 30 : 28,
                objectFit: 'contain',
                display: 'inline-block'
              }} />
            </a>
            <a href={settings && settings.PlayStoreLink ? settings.PlayStoreLink : '#'} style={{ display: 'inline-block' }} aria-label="Play Store">
              <img src={PlayStoreBadge} alt="Playstore Link" style={{
                height: compactMobile ? 30 : 28,
                objectFit: 'contain',
                display: 'inline-block'
              }} />
            </a>
          </div>
        </div>
      </div>
    </Box>
  );
}
